import { useState } from 'react'
import { Input } from '@components/ui/input'
import { Slider } from '@components/ui/slider'
import { Switch } from '@components/ui/switch'
import { ToggleGroup, ToggleGroupItem } from '@components/ui/toggle-group'

interface ShadowSliderRowProps {
  label: string
  value: number[]
  onValueChange: (value: number[]) => void
  placeholder: string
  disabled: boolean
}

function ShadowSliderRow({
  label,
  value,
  onValueChange,
  placeholder,
  disabled,
}: ShadowSliderRowProps) {
  return (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-foreground leading-normal">{label}</label>
      <div className="flex items-center gap-[18px]">
        <Slider
          value={value}
          onValueChange={onValueChange}
          min={0}
          max={100}
          step={1}
          className="flex-1 min-w-0"
          disabled={disabled}
        />
        <Input
          type="text"
          value={value[0]}
          onChange={(e) => onValueChange([parseInt(e.target.value) || 0])}
          size="compact"
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>
    </div>
  )
}

/**
 * Shadow switch, strength presets and Opacity / Offset / Angle rows. Placeholder UI: the state is
 * local and not applied to the document. The row values live here, above the `shadow &&`
 * conditional, so toggling Shadow off and on keeps them.
 */
export function ShadowControls({ disabled }: { disabled: boolean }) {
  const [shadow, setShadow] = useState(true)
  const [shadowOpacity, setShadowOpacity] = useState([30])
  const [shadowOffset, setShadowOffset] = useState([30])
  const [shadowAngle, setShadowAngle] = useState([30])
  const [selectedShadow, setSelectedShadow] = useState<string | null>('Subtle')

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <Switch checked={shadow} onCheckedChange={setShadow} disabled={disabled} />
        <label className="text-sm font-medium text-foreground leading-normal">Shadow</label>
      </div>

      {shadow && (
        <div className="mt-4 flex flex-col gap-3">
          {/* Shadow Strength */}
          <div>
            <ToggleGroup
              type="single"
              value={selectedShadow || ''}
              onValueChange={(val) => setSelectedShadow(val || null)}
              className="justify-start gap-1"
              disabled={disabled}
            >
              {['None', 'Subtle', 'Normal', 'Strong'].map((strength) => (
                <ToggleGroupItem
                  key={strength}
                  value={strength}
                  className="h-8 px-2 text-xs font-medium"
                >
                  {strength}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>

          {/* Shadow Properties */}
          <div className="flex flex-col gap-3">
            <ShadowSliderRow
              label="Opacity"
              value={shadowOpacity}
              onValueChange={setShadowOpacity}
              placeholder="Value %"
              disabled={disabled}
            />
            <ShadowSliderRow
              label="Offset"
              value={shadowOffset}
              onValueChange={setShadowOffset}
              placeholder="Value pt"
              disabled={disabled}
            />
            <ShadowSliderRow
              label="Angle"
              value={shadowAngle}
              onValueChange={setShadowAngle}
              placeholder="Value %"
              disabled={disabled}
            />
          </div>
        </div>
      )}
    </div>
  )
}
